import { Button, Modal } from 'antd'
import React, { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import {
  trackFoodSafetyPaywallShown,
  trackFoodSafetyPaywallUpgradeClicked,
} from 'src/utils/foodSafetyAnalytics'
import { buildFoodSafetyReportsPaywallCopy } from 'src/utils/foodSafetyReports'

/**
 * High-intent Starter paywall (benefit-led; View plans + human demo row) for Older / Download / Email — never blocks logging.
 * Secondary: Preview / email a sample report (fictional kitchen) so Free users see the value.
 */
const FoodSafetyReportsPaywallModal = ({
  visible,
  reason,
  olderRecordCount,
  upgradeHint,
  onClose,
  onPreviewSample,
  previewingSample = false,
  onEmailSample,
  emailingSample = false,
}) => {
  const history = useHistory()
  const { venueId } = useParams()
  const copy = buildFoodSafetyReportsPaywallCopy({
    reason,
    olderRecordCount,
    upgradeHint,
  })

  useEffect(() => {
    if (!visible) return undefined
    trackFoodSafetyPaywallShown({ reason })
    return undefined
  }, [visible, reason])

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
    >
      {/* antd3 Modal drops data-testid — land it on the body/hero */}
      <Hero data-testid='food-safety-reports-paywall'>
        <H3>{copy.title}</H3>
        <BodyText>{copy.body}</BodyText>
      </Hero>
      <Actions>
        <Button
          type='primary'
          size='large'
          data-testid='food-safety-reports-upgrade'
          onClick={() => {
            trackFoodSafetyPaywallUpgradeClicked({ reason })
            onClose?.()
            history.push(`/v/${venueId}/subscriptions/oms-plans`)
          }}
        >
          {copy.primaryLabel}
        </Button>
        {copy.sampleEmailLabel && onEmailSample ? (
          <Button
            size='large'
            loading={emailingSample}
            data-testid='food-safety-reports-paywall-sample-email'
            onClick={onEmailSample}
          >
            {copy.sampleEmailLabel}
          </Button>
        ) : null}
        {copy.sampleLabel && onPreviewSample ? (
          <Button
            size='large'
            loading={previewingSample}
            data-testid='food-safety-reports-paywall-sample'
            onClick={onPreviewSample}
          >
            {copy.sampleLabel}
          </Button>
        ) : null}
        <Button type='link' onClick={onClose}>
          Maybe later
        </Button>
      </Actions>
      <GrowthEngineHumanDemoRow compact featureLabel='Food Safety' />
    </Modal>
  )
}

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.xs};
`

export default FoodSafetyReportsPaywallModal
